/**
 * Clinic selections are NOT persisted.
 *
 * The clinic a referral belongs to comes from one of two places, both scoped to
 * the current page load:
 *   - the clinicCode in the URL path (/{clinicCode})
 *   - the user picking a clinic in the search box
 *
 * Nothing is written to localStorage, sessionStorage or cookies, so opening the
 * bare base URL always starts clean and asks which clinic the referral is for.
 *
 * Earlier versions did persist the selection - first the entire clinic object
 * under 'selectedClinic' with no expiry, later just a clinicCode under
 * 'selectedClinic.v2'. Both meant a browser that had visited one clinic would
 * silently attribute a later referral to it: the form restored the remembered
 * clinic instead of asking, and in the original version restored a clinic *name*
 * that could predate a rename in Foundation by a year.
 *
 * This module exists only to delete those leftovers from browsers that still
 * carry them.
 */

// Keys written by previous versions. Read by nothing - only removed.
const OBSOLETE_STORAGE_KEYS = ['selectedClinic', 'selectedClinic.v2'];

// Written by an old version of SearchClinic with a 30-day expiry, never read.
const OBSOLETE_COOKIE_NAMES = ['clinicName'];

/**
 * Deletes every clinic selection a previous version of this app persisted.
 * Safe to call on every page load.
 */
export const purgeClinicStorage = (): void => {
    try {
        for (const key of OBSOLETE_STORAGE_KEYS) {
            localStorage.removeItem(key);
        }
    } catch (error) {
        console.error('Error purging stored clinic selection:', error);
    }

    try {
        for (const name of OBSOLETE_COOKIE_NAMES) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
    } catch (error) {
        console.error('Error clearing clinic cookie:', error);
    }
};
